import React from "react";
import { Box, Button, Tooltip } from "@mui/material";

export interface RelatedBadge {
  count: number;
  href: string;
  tooltip: string;
}

// Sized for readability while still fitting a compact-density row (~36px)
const BUTTON_SX = {
  minWidth: 0,
  px: 0.75,
  py: 0,
  gap: 0.5,
  fontSize: "1rem",
  lineHeight: 1.6,
  whiteSpace: "nowrap",
} as const;

// The symbols are drawn as SVG rather than text: = and ≋ sit at the font's math
// axis rather than mid-digit, and ≋ often falls back to another font, so as
// text they rendered off-center from the count. SVG centers exactly and looks
// the same on every machine.
const ICON_SIZE = 22;

function SymbolIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

const EqualIcon = () => (
  <SymbolIcon>
    <path d="M4 8.5h16M4 15.5h16" />
  </SymbolIcon>
);

// Three waves, each spanning x 3-21 and centered on y 6, 12 and 18
const SharedIcon = () => (
  <SymbolIcon>
    {[6, 12, 18].map((y) => (
      <path key={y} d={`M3 ${y}q4.5 -4 9 0t9 0`} />
    ))}
  </SymbolIcon>
);

// Keep clicks from selecting the row or starting an edit
const stopPropagation = (event: React.SyntheticEvent) =>
  event.stopPropagation();

function Badge({
  icon,
  badge,
}: {
  icon: React.ReactNode;
  badge: RelatedBadge;
}) {
  return (
    <Tooltip title={badge.tooltip}>
      <Button
        size="small"
        variant="outlined"
        href={badge.href}
        target="_blank"
        rel="noreferrer"
        onClick={stopPropagation}
        onDoubleClick={stopPropagation}
        sx={BUTTON_SX}
      >
        {icon}
        {badge.count}
      </Button>
    </Tooltip>
  );
}

// Equal (=) and Shared (≋) badges for the Pricetool Related columns. Both are
// always shown, including at 0, since an empty result is itself informative.
export function RelatedBadges({
  equal,
  shared,
}: {
  equal: RelatedBadge;
  shared: RelatedBadge;
}) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, height: "100%" }}
    >
      <Badge icon={<EqualIcon />} badge={equal} />
      <Badge icon={<SharedIcon />} badge={shared} />
    </Box>
  );
}
